import CrudApiClient from './crudapiclient';
import { GATEWAY_API_URL } from '@admin-panel/config/env';
import { CrudFilters, CrudSort, MetaQuery } from '@refinedev/core';
import { axiosInstance } from './axios';
import { ReturnCourierExceptionResolutionStatus, ReturnStatus } from '@admin-panel/types';

class ReturnAPIClient extends CrudApiClient {
  constructor() {
    super(GATEWAY_API_URL + '/order/return');
  }

  async performAction(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      let url = this.baseURL + '/' + meta?.returnId + '/transition';
      if (payload.status == ReturnStatus.CANCELLED) {
        url = this.baseURL + '/' + meta?.returnId + '/cancel';
      }

      console.log('Calling return action api', url);

      const response = await axiosInstance.put(`${url}`, payload);
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async regenerateShiprocketPickup(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const response = await axiosInstance.put(
      `${this.baseURL}/${meta?.returnId}/shiprocket-pickup/regenerate`,
      payload,
    );
    return response.data?.data;
  }

  async replaceShiprocketPickup(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const response = await axiosInstance.put(
      `${this.baseURL}/${meta?.returnId}/shiprocket-pickup/replace`,
      payload,
    );
    return response.data?.data;
  }

  async findPickupCoordinationOptions(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const response = await axiosInstance.get(
      `${this.baseURL}/${meta?.returnId}/pickup-coordination/options`,
    );
    if (response.data?.success === false || response.data?.status?.type === 'ERROR') {
      throw new Error(
        response.data?.status?.message || 'Unable to check other open return pickups.',
      );
    }
    return response.data?.data || [];
  }

  async useExistingPickup(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const response = await axiosInstance.put(
      `${this.baseURL}/${meta?.returnId}/pickup-coordination`,
      payload,
    );
    if (response.data?.success === false || response.data?.status?.type === 'ERROR') {
      throw new Error(response.data?.status?.message || 'Unable to use the existing pickup.');
    }
    return this.transformNode(response.data?.data);
  }

  async findCourierEvidence(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const response = await axiosInstance.get(`${this.baseURL}/${meta?.returnId}/courier-evidence`);
    return response.data?.data;
  }

  async listCancelledAwbMovementExceptions(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const page = Number(meta?.page || meta?.currentPage || 1);
    const size = Number(meta?.pageSize || 50);
    const resolutionStatus = meta?.resolutionStatus || ReturnCourierExceptionResolutionStatus.OPEN;
    const params = new URLSearchParams({
      resolutionStatus: String(resolutionStatus),
      page: String(page),
      size: String(size),
    });
    const response = await axiosInstance.get(
      `${this.baseURL}/exceptions/cancelled-awb-movement?${params.toString()}`,
    );
    return {
      data: response.data?.data || [],
      total: response.data?.status?.count || 0,
    };
  }

  async findCancelledAwbMovementExceptionsForReturn(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const response = await axiosInstance.get(
      `${this.baseURL}/${meta?.returnId}/exceptions/cancelled-awb-movement`,
    );
    return response.data?.data || [];
  }

  async migrateCancelledAwbTracking(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const response = await axiosInstance.put(
      `${this.baseURL}/${meta?.returnId}/migrate-cancelled-awb`,
      payload,
    );
    return this.transformNode(response.data?.data);
  }

  async closeCancelledAwbMovementException(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const response = await axiosInstance.put(
      `${this.baseURL}/exceptions/cancelled-awb-movement/${meta?.exceptionId}/close`,
      payload,
    );
    return this.transformNode(response.data?.data);
  }
}

export default ReturnAPIClient;
